import { fmtFull } from '@concord/domain';
import { Button, Crumbs, Dialog, Field, Notice, PageHead, Panel, PanelHead, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { ME_KEY, type Me, safeNext } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { GetTheApp } from './GetTheApp';
import { Qr } from './Qr';
import s from './security.module.css';

interface Status {
  enabled: boolean;
  enabledAt: string | null;
  required: boolean;
  prompt: boolean;
}

/** "ABCD EFGH IJKL …": the manual key, in groups of four for typing. */
const grouped = (k: string) => k.replace(/(.{4})/g, '$1 ').trim();

function CodeInput({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  return (
    <input
      id={id}
      className="input code-input"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
    />
  );
}

/**
 * The navigator's sign-in security: an authenticator app (Google Authenticator or Microsoft
 * Authenticator), required for staff on staging and production. With it, every sign-in takes
 * the emailed code and then the app's code. Reached from the top bar, and right after sign-in.
 */
export function SecurityPage({ me }: { me: Me }) {
  const [params] = useSearchParams();
  const welcome = params.get('setup') === '1';
  const next = safeNext(params.get('next'), '/console');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const status = useQuery({
    queryKey: ['authenticator'],
    queryFn: () => api<Status>('/staff/authenticator'),
  });
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const [turningOff, setTurningOff] = useState(false);
  const started = useRef(false);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['authenticator'] });
    await qc.invalidateQueries({ queryKey: ME_KEY });
  };
  const begin = useMutation({
    mutationFn: () => api<{ secret: string; uri: string }>('/staff/authenticator/setup', { body: {} }),
    onSuccess: (r) => {
      setSetup(r);
      setCode('');
    },
  });
  const confirm = useMutation({
    mutationFn: () => api('/staff/authenticator/confirm', { body: { code } }),
    onSuccess: async () => {
      // Re-read the account first, so Continue never leaves with the old "not set up" state.
      await refresh();
      setSetup(null);
      setDone(true);
    },
  });
  const dismiss = useMutation({
    mutationFn: () => api('/staff/authenticator/dismiss', { body: {} }),
    onSettled: async () => {
      await qc.invalidateQueries({ queryKey: ME_KEY });
      navigate(next, { replace: true });
    },
  });
  const off = useMutation({
    mutationFn: (c: string) => api('/staff/authenticator/disable', { body: { code: c } }),
    onSuccess: async () => {
      setTurningOff(false);
      await refresh();
      toast('Authenticator app turned off. Sign in with an emailed code.');
    },
  });

  // The step after sign-in starts straight at the QR code.
  useEffect(() => {
    if (welcome && !started.current && status.data && !status.data.enabled) {
      started.current = true;
      begin.mutate();
    }
  }, [welcome, status.data, begin]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (code.length === 6) confirm.mutate();
  };
  const st = status.data;
  const required = !!(st?.required ?? me.authenticator?.required);
  const skippable = welcome && !required;

  return (
    <ConsoleLayout me={me} current="security">
      {!welcome && <Crumbs items={[{ label: 'Sign-in security' }]} />}
      <PageHead
        title={
          welcome
            ? required
              ? 'Set up your authenticator app'
              : 'Sign in faster with an authenticator app'
            : 'Sign-in security'
        }
        when={
          welcome
            ? required
              ? 'Required for every staff account before you use the console. It takes about a minute: install the app, scan the code, enter the number it shows.'
              : 'Optional here. Once it is set up, sign in with your email and the code your app shows; no email to wait for.'
            : 'Sign in with your email and the code from your authenticator app, or with an emailed code followed by the app code.'
        }
      />
      {status.isError && <Notice tone="bad">{errorMessage(status.error)}</Notice>}

      {done && (
        <Panel aria-label="Authenticator ready">
          <div className={s.done}>
            <strong>Your authenticator app is set up.</strong>
            <p>
              Next time, choose <b>Use my authenticator app</b> on the sign-in page and enter your email and
              the code it shows. If you use an emailed code instead, Concord also asks for the app code.
            </p>
            {welcome && <Button onClick={() => navigate(next, { replace: true })}>Continue</Button>}
          </div>
        </Panel>
      )}

      {st && !done && !setup && (
        <Panel aria-label="Authenticator app">
          <PanelHead title="Authenticator app" sub={st.enabled ? 'On' : 'Not set up'} />
          <div className={s.body}>
            {st.enabled ? (
              <>
                <p>
                  On since {fmtFull(new Date(st.enabledAt!), me.timezone)}. On the sign-in page, choose{' '}
                  <b>Use my authenticator app</b>.
                </p>
                {welcome && (
                  <div className={s.row}>
                    <Button onClick={() => navigate(next, { replace: true })}>Continue</Button>
                  </div>
                )}
                {required ? (
                  <p className="sub2">
                    Staff accounts keep their authenticator app. Lost your phone? Ask an admin to reset it.
                  </p>
                ) : (
                  <div className={s.row}>
                    <Button variant="sec" onClick={() => setTurningOff(true)}>
                      Turn off
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <GetTheApp />
                <div className={s.row}>
                  <Button busy={begin.isPending} onClick={() => begin.mutate()}>
                    Set up an authenticator app
                  </Button>
                  {skippable && (
                    <Button variant="ghost" busy={dismiss.isPending} onClick={() => dismiss.mutate()}>
                      Not now
                    </Button>
                  )}
                </div>
              </>
            )}
            {begin.isError && <Notice tone="bad">{errorMessage(begin.error)}</Notice>}
          </div>
        </Panel>
      )}

      {setup && (
        <Panel aria-label="Set up your authenticator app">
          <PanelHead title="Set up your authenticator app" />
          <div className={s.setup}>
            <div className={s.qr}>
              <Qr text={setup.uri} label="QR code for your authenticator app" />
            </div>
            <ol className={s.steps}>
              <li>
                Install Google Authenticator or Microsoft Authenticator on your phone, open it and add an
                account (in Google Authenticator, the + button; in Microsoft Authenticator, Add account, then
                Other account).
                <GetTheApp intro={false} />
              </li>
              <li>
                Scan this QR code. Can't scan? Choose to enter a key and type:
                <input
                  className={`input ${s.key}`}
                  aria-label="Setup key"
                  readOnly
                  value={grouped(setup.secret)}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </li>
              <li>
                <form onSubmit={submit} className={s.confirm}>
                  <Field label="Enter the 6-digit code your app shows">
                    {(id) => <CodeInput id={id} value={code} onChange={setCode} />}
                  </Field>
                  <div className={s.row}>
                    <Button type="submit" busy={confirm.isPending} disabled={code.length !== 6}>
                      Turn on
                    </Button>
                    {skippable && (
                      <Button variant="ghost" busy={dismiss.isPending} onClick={() => dismiss.mutate()}>
                        Not now
                      </Button>
                    )}
                  </div>
                  {confirm.isError && <Notice tone="bad">{errorMessage(confirm.error)}</Notice>}
                </form>
              </li>
            </ol>
          </div>
        </Panel>
      )}

      <TurnOff
        open={turningOff}
        onOpenChange={setTurningOff}
        busy={off.isPending}
        error={off.isError ? errorMessage(off.error) : null}
        onConfirm={(c) => off.mutate(c)}
      />
    </ConsoleLayout>
  );
}

function TurnOff({
  open,
  onOpenChange,
  busy,
  error,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  busy: boolean;
  error: string | null;
  onConfirm: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Turn off the authenticator app?"
      description="You will sign in with an emailed code only. Enter the code your app shows now to confirm."
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="danger" busy={busy} disabled={code.length !== 6} onClick={() => onConfirm(code)}>
            Turn off
          </Button>
        </>
      }
    >
      <Field label="6-digit code from your app">
        {(id) => <CodeInput id={id} value={code} onChange={setCode} />}
      </Field>
      {error && <Notice tone="bad">{error}</Notice>}
    </Dialog>
  );
}
